const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8788';

// Dummy data for development
const dummyCategories = [
  { id: '1', name: 'Restoran & Cafe', icon: '🍽️' },
  { id: '2', name: 'Sağlık Hizmetleri', icon: '🏥' },
  { id: '3', name: 'Eğitim', icon: '🎓' },
  { id: '4', name: 'Teknoloji', icon: '💻' },
  { id: '5', name: 'Güzellik & Bakım', icon: '💄' },
  { id: '6', name: 'Otomotiv', icon: '🚗' }
];

const dummyFirms = [];

const turkishCities = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Amasya', 'Ankara', 'Antalya', 'Artvin',
  'Aydın', 'Balıkesir', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa',
  'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Edirne', 'Elazığ', 'Erzincan',
  'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkâri', 'Hatay', 'Isparta',
  'Mersin', 'İstanbul', 'İzmir', 'Kars', 'Kastamonu', 'Kayseri', 'Kırklareli', 'Kırşehir',
  'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Kahramanmaraş', 'Mardin', 'Muğla',
  'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Rize', 'Sakarya', 'Samsun', 'Siirt',
  'Sinop', 'Sivas', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Şanlıurfa', 'Uşak',
  'Van', 'Yozgat', 'Zonguldak', 'Aksaray', 'Bayburt', 'Karaman', 'Kırıkkale', 'Batman',
  'Şırnak', 'Bartın', 'Ardahan', 'Iğdır', 'Yalova', 'Karabük', 'Kilis', 'Osmaniye', 'Düzce'
];

// API functions with dummy data fallback
export const api = {
  // Categories
  getCategories: async () => {
    try {
      const response = await fetch(`${API_BASE}/api/categories`);
      if (!response.ok) throw new Error('API not available');
      return await response.json();
    } catch {
      return dummyCategories;
    }
  },

  // Featured firms
  getFeaturedFirms: async (city = '') => {
    try {
      const response = await fetch(`${API_BASE}/api/featured?city=${city}`);
      if (!response.ok) throw new Error('API not available');
      return await response.json();
    } catch {
      return dummyFirms.filter(firm => !city || firm.sehir === city);
    }
  },

  // Firms by category
  getFirmsByCategory: async (categoryId, city = '') => {
    try {
      const response = await fetch(`${API_BASE}/api/firms/category/${categoryId}?city=${city}`);
      if (!response.ok) throw new Error('API not available');
      return await response.json();
    } catch {
      const category = dummyCategories.find(c => c.id === categoryId);
      return dummyFirms.filter(firm => 
        firm.kategori === category?.name && (!city || firm.sehir === city)
      );
    }
  },

  // Single firm
  getFirm: async (firmId) => {
    try {
      const response = await fetch(`${API_BASE}/api/firms/${firmId}`);
      if (!response.ok) throw new Error('API not available');
      return await response.json();
    } catch {
      return dummyFirms.find(firm => firm.id === firmId);
    }
  },

  // Add firm
  addFirm: async (firmData) => {
    try {
      const response = await fetch(`${API_BASE}/api/firms/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(firmData)
      });
      const data = await response.json().catch(() => ({ success: false, message: 'Sunucudan geçersiz yanıt' }));
      return data;
    } catch {
      return { success: false, message: 'Bağlantı hatası. Lütfen daha sonra tekrar deneyin.' };
    }
  },

  // Add comment
  addComment: async (firmId, commentData) => {
    try {
      const response = await fetch(`${API_BASE}/api/comments/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firmId, ...commentData })
      });
      if (!response.ok) throw new Error('API not available');
      return await response.json();
    } catch {
      return { success: true, message: 'Yorumunuz başarıyla gönderildi (demo mode)' };
    }
  },

  // Add complaint
  addComplaint: async (firmId, complaintData) => {
    try {
      const response = await fetch(`${API_BASE}/api/complaints/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firmId, ...complaintData })
      });
      if (!response.ok) throw new Error('API not available');
      return await response.json();
    } catch {
      return { success: true, message: 'Şikayetiniz başarıyla gönderildi (demo mode)' };
    }
  },

  // Contact form
  submitContact: async (contactData) => {
    try {
      const response = await fetch(`${API_BASE}/api/contact/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactData)
      });
      const data = await response.json().catch(() => ({ success: false, message: 'Sunucudan geçersiz yanıt' }));
      return data;
    } catch {
      return { success: false, message: 'Bağlantı hatası. Lütfen daha sonra tekrar deneyin.' };
    }
  },

  // Cities
  getCities: () => turkishCities,

  // Admin APIs (dummy responses)
  admin: {
    getPendingFirms: async () => {
      return [];
    },
    approveFirm: async (firmId) => {
      return { success: true, message: 'Firma onaylandı' };
    },
    disapproveFirm: async (firmId) => {
      return { success: true, message: 'Firma reddedildi' };
    },
    getPendingComments: async () => {
      return [{ id: 'comment1', firmId: '1', name: 'Ahmet Y.', comment: 'Harika hizmet', status: 'pending' }];
    },
    approveComment: async (commentId) => {
      return { success: true, message: 'Yorum onaylandı' };
    },
    deleteComment: async (commentId) => {
      return { success: true, message: 'Yorum silindi' };
    }
  }
};